import React, { useState, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LEADERSHIP_PROFILES, 
  CULTURE_PROFILES, 
  getProfileType, 
  analyzeRiskPatterns, 
  getRecommendedActions 
} from '../data/cultureLeadershipLogic';
import { departmentData } from '../data/departmentRiskLogic';

export const CultureLeadershipProfile = () => {
  const [selectedDeptId, setSelectedDeptId] = useState(departmentData[0].id);
  const selectedDept = useMemo(() => departmentData.find(d => d.id === selectedDeptId), [selectedDeptId]);

  // Mock utvidede metrics for profilanalyse
  const extendedMetrics = useMemo(() => ({
    ...selectedDept.metrics,
    leadership: selectedDept.metrics.we * 0.9,
    communication: 45,
    psychSafety: selectedDept.metrics.safety,
    trust: 48,
    roleClarity: 52,
    collaboration: 55,
    feedback: 38,
    changeReadiness: 42
  }), [selectedDept]);

  const leadershipProfile = getProfileType(extendedMetrics.leadership, LEADERSHIP_PROFILES);
  const cultureProfile = getProfileType(selectedDept.metrics.we, CULTURE_PROFILES);
  const riskPatterns = analyzeRiskPatterns(extendedMetrics);
  const actions = getRecommendedActions(riskPatterns, extendedMetrics.leadership, selectedDept.metrics.we);

  const radarOption = {
    radar: {
      indicator: [
        { name: 'Tydelighet', max: 100 },
        { name: 'Støtte', max: 100 },
        { name: 'Rettferdighet', max: 100 },
        { name: 'Tilgjengelighet', max: 100 },
        { name: 'Utvikling', max: 100 },
        { name: 'Konfliktevne', max: 100 }
      ],
      shape: 'circle',
      name: { textStyle: { color: '#64748b', fontSize: 10, fontWeight: 'bold' } }
    },
    series: [{
      type: 'radar',
      data: [{
        value: [65, 48, 72, 40, 55, 35],
        name: 'Leadership Profile',
        itemStyle: { color: '#6366f1' },
        areaStyle: { opacity: 0.2 }
      }]
    }]
  };

  const cultureRadarOption = {
    radar: {
      indicator: [
        { name: 'Tillit', max: 100 },
        { name: 'Respekt', max: 100 },
        { name: 'Ytring', max: 100 },
        { name: 'Læring', max: 100 },
        { name: 'Inkludering', max: 100 },
        { name: 'Samarbeid', max: 100 }
      ],
      shape: 'circle',
      name: { textStyle: { color: '#64748b', fontSize: 10, fontWeight: 'bold' } }
    },
    series: [{
      type: 'radar',
      data: [{
        value: [48, 65, 35, 50, 60, 55],
        name: 'Culture Profile',
        itemStyle: { color: '#10b981' },
        areaStyle: { opacity: 0.2 }
      }]
    }]
  };

  return (
    <div className="space-y-8 pb-20">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Culture & Leadership Profile</h1>
          <p className="text-slate-500 mt-1">Analyse av sammenhengen mellom lederatferd og organisasjonskultur.</p>
        </div>
        <div className="flex gap-2">
          <select 
            className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500/20"
            value={selectedDeptId}
            onChange={(e) => setSelectedDeptId(e.target.value)}
          >
            {departmentData.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <Button variant="primary" icon={<SafeIcon name="Download" />}>Eksporter Profil</Button>
        </div>
      </div>

      {/* Profile Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <Card className={`border-t-4 border-t-${leadershipProfile.color}-500 shadow-xl overflow-hidden group`}>
          <CardContent className="p-8">
            <div className="flex justify-between items-start mb-6">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Leadership Maturity</p>
                <h2 className={`text-2xl font-black text-${leadershipProfile.color}-600 uppercase tracking-tight`}>
                  {leadershipProfile.label}
                </h2>
              </div>
              <div className={`w-14 h-14 rounded-2xl bg-${leadershipProfile.color}-50 text-${leadershipProfile.color}-600 flex items-center justify-center shadow-sm`}>
                <SafeIcon name="Award" size={28} />
              </div>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed min-h-[48px]">{leadershipProfile.desc}</p>
            <div className="mt-8 h-48 bg-slate-50 rounded-3xl p-4">
              <ReactECharts option={radarOption} style={{ height: '100%' }} />
            </div>
          </CardContent>
        </Card>

        <Card className={`border-t-4 border-t-${cultureProfile.color}-500 shadow-xl overflow-hidden group`}>
          <CardContent className="p-8">
            <div className="flex justify-between items-start mb-6">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Culture Maturity</p>
                <h2 className={`text-2xl font-black text-${cultureProfile.color}-600 uppercase tracking-tight`}>
                  {cultureProfile.label}
                </h2>
              </div>
              <div className={`w-14 h-14 rounded-2xl bg-${cultureProfile.color}-50 text-${cultureProfile.color}-600 flex items-center justify-center shadow-sm`}>
                <SafeIcon name="Globe" size={28} />
              </div>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed min-h-[48px]">{cultureProfile.desc}</p>
            <div className="mt-8 h-48 bg-slate-50 rounded-3xl p-4">
              <ReactECharts option={cultureRadarOption} style={{ height: '100%' }} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Risk Pattern Analysis */}
      <div className="space-y-4">
        <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest ml-1">Risk Pattern Analysis</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {riskPatterns.map((pattern) => (
            <Card key={pattern.id} className="border-none shadow-md bg-white hover:translate-y-[-4px] transition-transform">
              <CardContent className="p-6 space-y-4">
                <div className="flex justify-between items-start">
                  <div className={`p-2 rounded-xl ${pattern.severity === 'Critical' ? 'bg-rose-50 text-rose-600' : 'bg-orange-50 text-orange-600'}`}>
                    <SafeIcon name={pattern.icon} />
                  </div>
                  <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded ${
                    pattern.severity === 'Critical' ? 'bg-rose-500 text-white' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {pattern.severity}
                  </span>
                </div>
                <h4 className="font-bold text-slate-900 leading-tight">{pattern.title}</h4>
                <p className="text-[11px] text-slate-500 leading-relaxed">{pattern.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Recommended Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest ml-1">Recommended Culture-Building Actions</h3>
          <Card className="border-none shadow-xl">
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {actions.map((action, i) => (
                  <div key={i} className="p-6 flex gap-6 items-center hover:bg-slate-50 transition-colors group">
                    <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0 group-hover:bg-indigo-600 group-hover:text-white transition-all">
                      <SafeIcon name="Zap" />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <h5 className="font-bold text-slate-900">{action.title}</h5>
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{action.type}</span>
                      </div>
                      <p className="text-xs text-slate-500">{action.desc}</p>
                    </div>
                    <Button variant="ghost" size="sm" icon={<SafeIcon name="ArrowRight" />} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="bg-slate-900 border-none text-white overflow-hidden">
            <CardHeader title="Dashboard View" />
            <CardContent className="space-y-6">
              {[
                { label: 'Leadership Profile Score', val: extendedMetrics.leadership, color: 'indigo' },
                { label: 'Culture Profile Score', val: selectedDept.metrics.we, color: 'emerald' },
                { label: 'Team Trust Score', val: extendedMetrics.trust, color: 'cyan' },
                { label: 'Communication Maturity', val: extendedMetrics.communication, color: 'indigo' }
              ].map((m, i) => (
                <div key={i} className="space-y-2">
                  <div className="flex justify-between text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    <span>{m.label}</span>
                    <span className="text-white">{m.val}%</span>
                  </div>
                  <div className="w-full bg-white/10 h-1.5 rounded-full overflow-hidden">
                    <div className={`h-full bg-${m.color}-500 transition-all duration-1000`} style={{ width: `${m.val}%` }} />
                  </div>
                </div>
              ))}
              <div className="pt-4 border-t border-white/5">
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">Leadership Maturity Level</p>
                <div className="flex gap-1.5">
                  {[1, 2, 3, 4, 5].map(lvl => (
                    <div 
                      key={lvl} 
                      className={`flex-1 h-2 rounded-full transition-all ${
                        lvl <= leadershipProfile.level ? `bg-${leadershipProfile.color}-500` : 'bg-white/10'
                      }`} 
                    />
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-emerald-600 text-white border-none p-2 shadow-xl shadow-emerald-100">
            <CardContent className="p-6 space-y-4">
              <SafeIcon name="TrendingUp" className="text-4xl text-emerald-200" />
              <h4 className="text-xl font-black uppercase tracking-tighter">Strength Patterns</h4>
              <p className="text-xs text-emerald-50 leading-relaxed">
                Avdelingen viser en sterk evne til <b>Inkludering</b> og <b>Samarbeid</b>, som fungerer som en beskyttende buffer mot operativt stress.
              </p>
              <Button variant="secondary" className="w-full bg-white text-emerald-600 border-none">Last ned Styrkerapport</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};